// Instructions phase — "Hướng dẫn chơi game".
// Bấm Tiếp tục → gọi POST /v1/game/ready (bắt đầu tính giờ) → Play.
// Strict mode: nếu gameReady trả 400 (invalid signature) → block, hiện lỗi.

import type { Phase } from "../state/types";
import { mountFrame, clearChildren } from "../ui/overlay";
import { apiBase, gameReady, ApiHttpError } from "../api/client";
import { getSignature } from "../crypto";
import { INGREDIENTS_BY_THEME } from "../assets/themeMap";
import { drawCharacterPreview } from "../game/renderer";

const FLOW = "/textures/flow";
const sharedUrl = (name: string) => `${apiBase()}/v1/shared/${name}`;

// Redesign per client ref: the theme's recipe ingredients in a big 2×2 grid
// (plain images — no borders/shadow), a dashed divider, then the character.
// Only INGREDIENTS_BY_THEME[theme] is shown — each theme has its own recipe.
// Grid sits entirely ABOVE the divider (ends ~48%, divider at 50%).
const INGREDIENT_GRID = "left:10%;right:10%;top:17%;height:31%;";
const DIVIDER_TOP = "50%";
// Each ingredient is a SQUARE = min(HALF% of the grid's width, HALF% of its
// height) via container-query units. A 2×2 of these fills the grid's height
// exactly, so it can never spill past the divider. 50 = max size (rows touch);
// lower for spacing between them.
const INGREDIENT_HALF = 50;
// Character canvas sits in the band below the dashed divider (50%), above the
// baked bottom text (~82%). drawCharacterPreview centers + min-fits the sprite
// inside this box (height is the limiting dimension), so `height` sets the
// character size and `top` its vertical placement. height 45% → character ~50%
// larger than the original 30%; top 48% seats it just under the divider
// (on-device tuned — nudge top up/down to reposition, height to resize).
const CHARACTER_BOX = "top:48%;height:45%;";

let cleanup: (() => void) | null = null;

function showError(root: HTMLElement, message: string, onRetry: () => void): void {
  const existing = root.querySelector("#instructions-error");
  if (existing) existing.remove();

  const modal = document.createElement("div");
  modal.id = "instructions-error";
  modal.style.cssText =
    "position:absolute;inset:0;z-index:30;background:#0009;display:flex;align-items:center;justify-content:center;padding:24px;";
  modal.innerHTML = `
    <div style="background:#fff;color:#222;border-radius:14px;padding:20px;max-width:90%;display:flex;flex-direction:column;gap:14px;align-items:center;">
      <div style="font-size:16px;font-weight:bold;">Không thể bắt đầu game</div>
      <div style="font-size:14px;line-height:1.5;text-align:center;">${message}</div>
      <button id="retry-btn" style="padding:12px 24px;border:0;border-radius:10px;background:#e34;color:#fff;font-size:15px;font-weight:bold;cursor:pointer;">Thử lại</button>
    </div>`;
  root.appendChild(modal);
  modal.querySelector<HTMLButtonElement>("#retry-btn")!.addEventListener("click", () => {
    modal.remove();
    onRetry();
  });
}

export const Instructions: Phase = {
  enter(ctx, transition) {
    const root = document.getElementById("app")!;
    clearChildren(root);
    const theme = ctx.theme ?? 0;

    const bg = document.createElement("div");
    bg.className = "app-bg";
    bg.style.backgroundImage = "url(/textures/bg_indexA.png)";
    bg.style.backgroundColor = "#f7c7d8";

    const frame = document.createElement("div");
    frame.className = "app-frame";
    frame.style.cssText =
      "position:relative;display:flex;flex-direction:column;align-items:center;padding:3% 4%;box-sizing:border-box;gap:2%;";

    const sq = `min(${INGREDIENT_HALF}cqw,${INGREDIENT_HALF}cqh)`;
    const ingredientIcons = (indices: readonly number[]): string =>
      indices
        .map(
          (i) =>
            `<img src="${sharedUrl(`ingredient_${i}.png`)}" crossorigin="anonymous" alt="" style="width:${sq};height:${sq};object-fit:contain;"/>`,
        )
        .join("");

    frame.innerHTML = `
      <div style="position:relative;width:100%;aspect-ratio:1500/2332;flex:0 0 auto;
        background:url(${FLOW}/fraameInstruction.png) center/100% 100% no-repeat;">
        <div style="position:absolute;${INGREDIENT_GRID}container-type:size;display:grid;
          grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;place-items:center;">${ingredientIcons(INGREDIENTS_BY_THEME[theme])}</div>
        <div style="position:absolute;left:8%;right:8%;top:${DIVIDER_TOP};border-top:3px dashed #e58aad;"></div>
        <canvas id="char" style="position:absolute;left:10%;width:80%;${CHARACTER_BOX}"></canvas>
      </div>
      <button id="continue" style="
        margin-top:auto;width:55%;background:none;border:0;padding:0;cursor:pointer;
        background-image:url(${FLOW}/buttonContinue.png);background-size:contain;background-repeat:no-repeat;
        background-position:center;aspect-ratio:612/208;" aria-label="Tiếp tục"></button>
    `;

    root.appendChild(bg);
    const frameCleanup = mountFrame(root, frame);
    cleanup = () => {
      if (bg.parentElement === root) root.removeChild(bg);
      frameCleanup();
    };

    const shared = ctx.assets.shared;
    const sprites = ctx.assets.characterSprites;
    if (shared && sprites) {
      const cv = frame.querySelector<HTMLCanvasElement>("#char")!;
      requestAnimationFrame(() => {
        const dpr = window.devicePixelRatio || 1;
        const rect = cv.getBoundingClientRect();
        cv.width = Math.max(1, Math.round(rect.width * dpr));
        cv.height = Math.max(1, Math.round(rect.height * dpr));
        const c2d = cv.getContext("2d");
        if (c2d) drawCharacterPreview(c2d, shared, sprites, shared.config.characterVisual, "neutral", 0, cv.width, cv.height);
      });
    }

    const continueBtn = frame.querySelector<HTMLButtonElement>("#continue")!;

    const doReady = async () => {
      continueBtn.style.pointerEvents = "none";
      continueBtn.style.opacity = "0.7";

      if (ctx.sessionId != null) {
        try {
          const sig = await getSignature(ctx.sessionId);
          ctx.signature = sig;
          await gameReady(sig);
        } catch (err) {
          console.error("[Instructions] gameReady failed", err);

          // Strict mode: block bất kể lỗi gì
          let msg = "Lỗi không xác định. Vui lòng thử lại.";
          if (err instanceof ApiHttpError) {
            switch (err.status) {
              case 400: msg = "Phiên chơi không hợp lệ (signature sai hoặc hết hạn)."; break;
              case 401: msg = "Bạn chưa được xác thực. Vui lòng tải lại trang."; break;
              case 403: msg = "Không có quyền truy cập phiên chơi này."; break;
              case 404: msg = "Không tìm thấy phiên chơi. Vui lòng bắt đầu lại."; break;
              case 429: msg = "Quá nhiều yêu cầu. Vui lòng chờ và thử lại."; break;
              case 500:
              case 502:
              case 503: msg = "Máy chủ đang gặp sự cố. Vui lòng thử lại sau."; break;
              default: msg = `Lỗi máy chủ (${err.status}). Vui lòng thử lại.`;
            }
          } else if (err instanceof TypeError && err.message.includes("fetch")) {
            msg = "Mất kết nối mạng. Vui lòng kiểm tra internet và thử lại.";
          } else {
            msg = "Có lỗi xảy ra. Vui lòng thử lại.";
          }

          showError(
            root,
            msg,
            () => {
              continueBtn.style.pointerEvents = "auto";
              continueBtn.style.opacity = "1";
            },
          );
          return; // Không transition sang Play
        }
      }

      transition("Play");
    };

    continueBtn.addEventListener("click", () => void doReady());
  },
  exit() {
    cleanup?.();
    cleanup = null;
  },
};
